/** @file Applies one command to a document with immer, returning the new doc. */
import { enablePatches, produceWithPatches, type Patch } from 'immer';
import type { CommandCall, Doc } from '@pose2d/format/types';
import { docIndex } from '@pose2d/format/doc/docIndex';
import { throwError } from '@pose2d/format/throwError';
import { parseOrThrow } from '@pose2d/format/parseOrThrow';
import type { CommandDef } from './defineCommand';
import type { CommandContext } from './commandContext';
import type { CommandResult } from './commandResult';
import { resolveRef } from './_shared/resolveRef';

enablePatches();

export interface ApplyOutcome {
  doc: Doc;
  patches: Patch[];
  inverse: Patch[];
  result: CommandResult;
}

export type CommandLookup = (name: string) => CommandDef<unknown> | undefined;

/** Everything the caller supplies: the registry lookup, the clock, the actor and an id minter. */
export interface ApplyEnv {
  lookup: CommandLookup;
  now: string;
  actor: string;
  newId: CommandContext['newId'];
}

/** Validates the input with the command's schema, applies it, and returns patches. Throws via throwError (toPose2DError reads it). */
export function applyCommand(doc: Doc, cmd: CommandCall, env: ApplyEnv): ApplyOutcome {
  const def = env.lookup(cmd.name) ?? throwError('NOT_FOUND', { kind: 'command', ref: cmd.name });
  const input = parseOrThrow(def.input, cmd.input);
  const index = docIndex(doc);
  const ctx: CommandContext = {
    index,
    resolve: (kind, ref) => resolveRef(index, kind, ref),
    newId: env.newId,
    now: env.now,
    actor: env.actor,
    warnings: [],
    created: {},
    lookup: (name) => env.lookup(name),
  };
  const [next, patches, inverse] = produceWithPatches(doc, (draft) => {
    def.apply(draft, input, ctx);
  });
  const result: CommandResult = {
    warnings: ctx.warnings,
    ...(Object.keys(ctx.created).length ? { created: ctx.created } : {}),
    ...(ctx.confidence === undefined ? {} : { confidence: ctx.confidence }),
  };
  return { doc: next, patches, inverse, result };
}
